import Moment from 'moment';
import { UserIdentityType } from '@/services/UserIdentitiesService';
import { isValidPersonName, PersonNameInvalidMessage } from '@/utils/personNameRules';

// Reglas puras de validación del formulario de datos del usuario en la compra de la suscripción.
// Los mensajes siguen hardcodeados como en el resto del componente (UserForm.tsx).

export type UserFormErrors = Partial<Record<UserFormTextField | 'birthday', string>>;

export type UserFormTextField = 'firstName' | 'lastName' | 'secondLastName' | 'email' | 'countryPrefix' | 'phone';

export const TextFields: UserFormTextField[] = ['firstName', 'lastName', 'secondLastName', 'email', 'countryPrefix', 'phone'];

// Campos de texto obligatorios (los mismos que exige el backend al crear el interviniente principal):
// el botón de continuar se deshabilita si alguno está vacío. El segundo apellido es opcional.
// La fecha de nacimiento se valida aparte (no vacía y mayor de edad) igual que hasta ahora.
export const RequiredTextFields: UserFormTextField[] = ['firstName', 'lastName', 'email', 'countryPrefix', 'phone'];

export const MinNameLength = 2;
export const MinPhoneDigits = 9;
export const AdultAge = 18;

// local@dominio.tld: la parte local admite letras, dígitos y . _ % + -; el dominio admite subdominios
// con guiones (nunca al principio o al final de cada etiqueta) y termina en un TLD de al menos 2 letras.
const EmailPattern = /^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/**
 * Indica si el email tiene un formato razonable (local@dominio.tld). Se ignoran los espacios de los extremos.
 */
export const isValidEmail = (value: string | null | undefined): boolean =>
{
    if(value === null || value === undefined)
    {
        return false;
    }
    const trimmed = value.trim();
    if(!EmailPattern.test(trimmed))
    {
        return false;
    }
    // La parte local no puede empezar ni acabar en punto ni tener puntos consecutivos.
    const localPart = trimmed.substring(0, trimmed.lastIndexOf('@'));
    return !localPart.startsWith('.') && !localPart.endsWith('.') && !localPart.includes('..');
};

/**
 * Indica si la persona nacida en la fecha indicada es mayor de edad en la fecha de referencia.
 */
export const isAdult = (birthday: Date, today: Date = new Date()): boolean =>
{
    return Moment(today).diff(Moment(birthday), 'years') >= AdultAge;
};

/**
 * Nombre o apellido: mínimo 2 caracteres y el formato de PersonNameRules (el mismo que aplica el backend al crear
 * el interviniente principal).
 */
export const validateName = (value: string | null | undefined): string | undefined =>
{
    if((value ?? '').trim().length < MinNameLength)
    {
        return `Debe contener al menos ${MinNameLength} caracteres`;
    }
    return isValidPersonName(value) ? undefined : PersonNameInvalidMessage;
};

/**
 * Nombre opcional (segundo apellido): vacío es válido; si se rellena, mínimo 2 caracteres.
 */
export const validateOptionalName = (value: string | null | undefined): string | undefined =>
{
    return (value ?? '').trim() === '' ? undefined : validateName(value);
};

export const validateEmail =(value: string | null | undefined): string | undefined =>
{
    return isValidEmail(value) ? undefined : "Formato no válido";
};

export const validatePhone = (value: string | null | undefined): string | undefined =>
{
    const digits = (value ?? '').replace(/\s+/g, '');
    if(!/^\d*$/.test(digits))
    {
        return "Solo puede contener dígitos";
    }
    return digits.length < MinPhoneDigits ? `Debe contener al menos ${MinPhoneDigits} dígitos` : undefined;
};

export const validateCountryPrefix = (value: string | null | undefined): string | undefined =>
{
    return (value ?? '').length === 0 ? "Debes seleccionar un prefijo" : undefined;
};

/**
 * Valida la fecha de nacimiento: no vacía y mayor de edad.
 */
export const validateBirthday = (value: Date | null | undefined, today: Date = new Date()): string | undefined =>
{
    if(value === null || value === undefined)
    {
        return "La fecha de nacimiento no puede estar vacía";
    }
    return isAdult(value, today) ? undefined : "El titular principal debe ser mayor de edad";
};

/**
 * Valida un campo de texto del formulario. Devuelve el mensaje de error o undefined si es válido.
 */
export const validateTextField = (name: string, value: string | null | undefined): string | undefined =>
{
    switch(name)
    {
        case 'firstName':
        case 'lastName':
            return validateName(value);
        case 'secondLastName':
            return validateOptionalName(value);
        case 'email':
            return validateEmail(value);
        case 'countryPrefix':
            return validateCountryPrefix(value);
        case 'phone':
            return validatePhone(value);
        default:
            return undefined;
    }
};

/**
 * Devuelve una copia de los errores con el del campo indicado fijado o eliminado (sin mutar el original).
 */
export const setFieldError = (errors: UserFormErrors, name: string, message: string | undefined): UserFormErrors =>
{
    const next: Record<string, string> = { ...errors } as Record<string, string>;
    if(message === undefined)
    {
        delete next[name];
    }
    else
    {
        next[name] = message;
    }
    return next as UserFormErrors;
};

/**
 * Errores iniciales: solo se validan los campos que ya vienen rellenos, para no marcar en rojo
 * los vacíos antes de que el usuario los toque.
 */
export const getInitialErrors = (identity: UserIdentityType): UserFormErrors =>
{
    return TextFields.reduce<UserFormErrors>((errors, field) =>
    {
        const value = identity[field] as string | undefined;
        if(value === undefined || value === null || value === '')
        {
            return errors;
        }
        return setFieldError(errors, field, validateTextField(field, value));
    }, {});
};

export const hasEmptyRequiredField = (identity: UserIdentityType): boolean =>
{
    return RequiredTextFields.some((field) =>
    {
        const value = identity[field] as string | undefined;
        return value === undefined || value === null || value.trim() === '';
    });
};

/**
 * El botón de continuar se deshabilita si falta algún campo obligatorio o hay algún error.
 */
export const shouldDisableSubmit = (identity: UserIdentityType, errors: UserFormErrors): boolean =>
{
    return hasEmptyRequiredField(identity) || Object.keys(errors).length > 0;
};
